import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

// The child buttons are rendered `controlled`, so they only report the intent
// through `selection-button-component:change` and this controller decides what
// the new selection is.
export default class extends Controller {
  static targets = ['button'];

  static values = {
    behaviour: { type: String, default: 'only_one' },
    selected: Array,
    name: String,
    autoSaveUrl: String,
    debounce: { type: Number, default: 0 }
  };

  connect() {
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.nameValue,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element
    });
  }

  disconnect() {
    this.autoSave.cancel();
  }

  // ── Public actions (wired from data-action) ──────────────────────────────────
  handleChange(event) {
    const { value, selected } = event.detail;

    if (this.isMulti) {
      const next = selected
        ? [...this.selectedValue, value]
        : this.selectedValue.filter(entry => entry !== value);

      // At least one option must remain selected.
      if (next.length === 0) return;

      this.emit(next);
      return;
    }

    // Only one: ignore "deselect" clicks; switch only when a different option
    // is picked.
    if (!selected) return;

    this.emit([value]);
  }

  // ── Internals ───────────────────────────────────────────────────────────────
  emit(next) {
    this.selectedValue = next;

    const payload = this.isMulti ? next : (next[0] || '');
    this.dispatch('change', { detail: { selected: payload } });
    if (this.autoSaveUrlValue !== '') this.autoSave.save(payload);
  }

  // Pushes the group's selection into every child.
  selectedValueChanged(selected) {
    this.buttonTargets.forEach(element => {
      const button = this.controllerFor(element);
      if (button) button.setSelected(selected.includes(button.optionValue));
    });
  }

  controllerFor(element) {
    return this.application.getControllerForElementAndIdentifier(
      element,
      'selection-button-component'
    );
  }

  get isMulti() {
    return this.behaviourValue === 'at_least_one';
  }
}
